import { useLocation, useNavigate } from 'react-router-dom';

import { BOARD_VIEWS, BoardViewKey, boardPath, viewKeyOf } from '@/constants/routes';
import { useAppData } from '@/app-data';
import { Popover, menuRow } from '@/components/ui/Popover';
import { Check, ChevronDown, KanbanIcon, TableIcon, TimelineIcon } from '@/components/ui/icons';
import { cn } from '@/components/ui/cn';

const viewIcon = (key: BoardViewKey, className?: string) => {
  if (key === 'timeline') return <TimelineIcon className={className} />;
  if (key === 'table') return <TableIcon className={className} />;
  return <KanbanIcon className={className} />;
};

/**
 * Kanban / Table / Timeline picker. On desktop it's a SEGMENTED PILL — all three always visible so
 * switching is a single click (the whole point when flipping between them rapidly). On mobile that
 * would eat three slots, so below `sm` it collapses to a single dropdown pill. Navigates to the
 * current board's friendly slug URL. Renders nothing off a board.
 */
export default function ViewSwitcher() {
  const { currentBoard } = useAppData();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  if (!currentBoard) return null;

  const segments = pathname.split('/').filter(Boolean);
  const currentKey = viewKeyOf(segments[1]);
  const currentView = BOARD_VIEWS.find((v) => v.key === currentKey);
  const go = (key: BoardViewKey) => {
    if (key !== currentKey) navigate(boardPath(currentBoard.slug, key));
  };

  return (
    <>
      {/* Desktop: segmented pill (≥ sm) */}
      <div className="hidden items-center gap-0.5 rounded-full bg-layer p-0.5 sm:inline-flex" role="tablist" aria-label="Board view">
        {BOARD_VIEWS.map((v) => {
          const isActive = v.key === currentKey;
          return (
            <button
              key={v.key}
              type="button"
              role="tab"
              aria-selected={isActive}
              aria-label={v.label}
              title={v.label}
              onClick={() => go(v.key)}
              className={cn(
                'inline-flex h-8 w-9 items-center justify-center rounded-full transition-colors',
                isActive ? 'bg-surface-raised text-fg-strong shadow-sm shadow-black/20' : 'text-fg-muted hover:text-fg',
              )}
            >
              {viewIcon(v.key, isActive ? 'text-fg-soft' : 'text-fg-faint')}
            </button>
          );
        })}
      </div>

      {/* Mobile: single dropdown pill (< sm) */}
      <div className="sm:hidden">
        <Popover
          trigger={({ open, toggle }) => (
            <button
              type="button"
              onClick={toggle}
              aria-haspopup="menu"
              aria-expanded={open}
              aria-label={`Board view: ${currentView?.label ?? 'Kanban'}`}
              className="inline-flex min-h-[36px] items-center gap-1.5 rounded-full bg-layer px-3 text-[13px] font-semibold text-fg-strong"
            >
              {viewIcon(currentKey, 'text-fg-soft')}
              <span>{currentView?.label ?? 'Kanban'}</span>
              <ChevronDown open={open} className="text-fg-faint" />
            </button>
          )}
        >
          {(close) => (
            <div className="min-w-[168px]">
              {BOARD_VIEWS.map((v) => {
                const isActive = v.key === currentKey;
                return (
                  <button
                    key={v.key}
                    type="button"
                    onClick={() => {
                      go(v.key);
                      close();
                    }}
                    className={cn(menuRow, isActive ? 'text-fg' : 'text-fg-soft')}
                  >
                    {viewIcon(v.key, 'text-fg-faint')}
                    <span className="flex-1">{v.label}</span>
                    <span className="flex w-3.5 shrink-0 justify-center text-accent">{isActive ? <Check /> : null}</span>
                  </button>
                );
              })}
            </div>
          )}
        </Popover>
      </div>
    </>
  );
}
